// apps/smart-shikka-web/src/surfaces/surfaces.tsx
// PR-CORE-11 — Projects / Files / Automations / Apps / Code / Design / Activity
// surfaces. Each reads only canonical durable endpoints; a denied or absent
// backend renders the truthful unavailable/empty state, never fake data.
import { useEffect, useState } from "react";
import { api } from "../api.js";
import { useAuth } from "../auth.js";
import { DesktopDownloadCta } from "../DesktopDownloadCta.js";
import {
  ActivityLedgerView,
  assistantTurnFromMessage,
  buildActivityLedger,
  productText,
  useBrandColors,
  type LedgerAssistantTurn,
  type LedgerApproval,
  type LedgerCoworkRun,
  type LedgerUsageEvent,
} from "@smart-shikka/react";
import { useLocale } from "../ui.js";
import {
  EmptyState,
  SurfacePage,
  SurfaceStateView,
  surfaceGetJson,
  useSurfaceQuery,
} from "./surfaceScaffold.js";

function cardStyle(C: ReturnType<typeof useBrandColors>) {
  return {
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    padding: "10px 14px",
    background: C.card,
  } as const;
}

function Grid(props: { children: React.ReactNode }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 10 }}>
      {props.children}
    </div>
  );
}

/* ── Projects ───────────────────────────────────────────────────────────── */

interface ProjectRow {
  projectId: string;
  displayName: string;
  repository: string;
  visibility: string;
  defaultBranch: string;
}

export function ProjectsSurface() {
  const C = useBrandColors();
  const { locale } = useLocale();
  const q = useSurfaceQuery<ProjectRow[]>("/api/projects", (d) =>
    (Array.isArray(d.projects) ? (d.projects as Record<string, unknown>[]) : []).map((p) => ({
      projectId: String(p.projectId ?? ""),
      displayName: String(p.displayName ?? p.projectId ?? ""),
      repository: String(p.repository ?? ""),
      visibility: String(p.visibility ?? ""),
      defaultBranch: String(p.defaultBranch ?? ""),
    })),
  );
  return (
    <SurfacePage
      route="projects"
      title={productText("navProjects", locale)}
      subtitle={locale === "hi" ? "कैनॉनिकल सर्वर प्रोजेक्ट्स" : "Canonical server projects"}
    >
      {q.kind === "ready" ? (
        q.data.length === 0 ? (
          <EmptyState />
        ) : (
          <Grid>
            {q.data.map((p) => (
              <div key={p.projectId} style={cardStyle(C)}>
                <div style={{ fontSize: 13, fontWeight: 650, color: C.fg }}>{p.displayName}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{p.repository}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
                  {p.visibility} · {p.defaultBranch}
                </div>
              </div>
            ))}
          </Grid>
        )
      ) : (
        <SurfaceStateView state={q} onRetry={q.retry} />
      )}
    </SurfacePage>
  );
}

/* ── Files ──────────────────────────────────────────────────────────────── */

interface LibraryRow {
  fileId: string;
  path: string;
  size: number;
  language?: string | undefined;
  sourceKind: "artifact" | "project_index";
  artifactSource?: string | undefined;
  extractionStatus: string;
  extractionMessage?: string | undefined;
  chunkCount: number;
  hasAppProvenance: boolean;
  retrievable: boolean;
  stale: boolean;
  lastIndexedAt?: number | undefined;
}

function knowledgeStatusLabel(status: string, locale: string): string {
  const hi = locale === "hi";
  switch (status) {
    case "extracted": return hi ? "निकाला गया" : "extracted";
    case "pending": return hi ? "प्रतीक्षा में" : "pending";
    case "unsupported": return hi ? "असमर्थित" : "unsupported";
    case "error": return hi ? "त्रुटि" : "error";
    default: return status;
  }
}

export function FilesSurface() {
  const C = useBrandColors();
  const { locale } = useLocale();
  const projectsQ = useSurfaceQuery<ProjectRow[]>("/api/projects", (d) =>
    (Array.isArray(d.projects) ? (d.projects as Record<string, unknown>[]) : []).map((p) => ({
      projectId: String(p.projectId ?? ""),
      displayName: String(p.displayName ?? p.projectId ?? ""),
      repository: String(p.repository ?? ""),
      visibility: String(p.visibility ?? ""),
      defaultBranch: String(p.defaultBranch ?? ""),
    })),
  );
  const [selected, setSelected] = useState<string>("");
  const projectId =
    selected || (projectsQ.kind === "ready" ? projectsQ.data[0]?.projectId ?? "" : "");
  const filesQ = useSurfaceQuery<LibraryRow[]>(
    projectId ? `/api/projects/${encodeURIComponent(projectId)}/knowledge/library` : null,
    (d) =>
      (Array.isArray(d.entries) ? (d.entries as Record<string, unknown>[]) : []).map((f) => ({
        fileId: String(f.fileId ?? ""),
        path: String(f.path ?? ""),
        size: typeof f.size === "number" ? f.size : 0,
        language: typeof f.language === "string" ? f.language : undefined,
        sourceKind: f.sourceKind === "artifact" ? "artifact" : "project_index",
        artifactSource: typeof f.artifactSource === "string" ? f.artifactSource : undefined,
        extractionStatus: String(f.extractionStatus ?? "pending"),
        extractionMessage: typeof f.extractionMessage === "string" ? f.extractionMessage : undefined,
        chunkCount: typeof f.chunkCount === "number" ? f.chunkCount : 0,
        hasAppProvenance: f.hasAppProvenance === true,
        retrievable: f.retrievable === true,
        stale: f.stale === true,
        lastIndexedAt: typeof f.lastIndexedAt === "number" ? f.lastIndexedAt : undefined,
      })),
  );

  return (
    <SurfacePage
      route="files"
      title={productText("navFiles", locale)}
      subtitle={locale === "hi" ? "Knowledge Library — प्रोजेक्ट sources की सच्ची स्थिति" : "Knowledge Library — truthful source lifecycle"}
    >
      {projectsQ.kind === "ready" && projectsQ.data.length === 0 ? (
        <EmptyState />
      ) : projectsQ.kind !== "ready" ? (
        <SurfaceStateView state={projectsQ} onRetry={projectsQ.retry} />
      ) : (
        <>
          <div style={{ marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
            <label htmlFor="ss-files-project" style={{ fontSize: 12, color: C.muted }}>
              {productText("navProjects", locale)}:
            </label>
            <select
              id="ss-files-project"
              value={projectId}
              onChange={(e) => setSelected(e.target.value)}
              style={{
                fontSize: 12,
                padding: "4px 8px",
                borderRadius: 8,
                border: `1px solid ${C.border}`,
                background: C.card,
                color: C.fg,
              }}
            >
              {projectsQ.data.map((p) => (
                <option key={p.projectId} value={p.projectId}>
                  {p.displayName}
                </option>
              ))}
            </select>
          </div>
          {filesQ.kind === "ready" ? (
            filesQ.data.length === 0 ? (
              <EmptyState />
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {filesQ.data.map((f) => (
                  <div
                    key={f.fileId}
                    style={{
                      ...cardStyle(C),
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                      <span style={{ fontSize: 12, color: C.fg, overflow: "hidden", textOverflow: "ellipsis" }}>
                        {f.path}
                      </span>
                      <span style={{ fontSize: 11, color: C.muted, whiteSpace: "nowrap" }}>
                        {f.language ? `${f.language} · ` : ""}
                        {(f.size / 1024).toFixed(1)} KB
                      </span>
                    </div>
                    <span style={{ fontSize: 11, color: C.muted }}>
                      {knowledgeStatusLabel(f.extractionStatus, locale)}
                      {" · "}
                      {f.chunkCount} chunks
                      {" · "}
                      {f.sourceKind === "artifact"
                        ? (locale === "hi" ? "अपलोड artifact" : "uploaded artifact") + (f.artifactSource ? ` (${f.artifactSource})` : "")
                        : (locale === "hi" ? "प्रोजेक्ट index" : "project index")}
                      {f.hasAppProvenance ? " · live-state app source" : ""}
                      {f.stale ? " · stale" : ""}
                    </span>
                    <span style={{ fontSize: 11, color: f.retrievable ? C.fg : C.muted }}>
                      {f.retrievable
                        ? (locale === "hi" ? "assistant context में उपलब्ध" : "available in assistant context")
                        : (locale === "hi" ? "assistant context में उपलब्ध नहीं" : "not in assistant context")}
                      {f.lastIndexedAt ? ` · ${new Date(f.lastIndexedAt).toISOString().slice(0, 10)}` : ""}
                    </span>
                    {(f.extractionStatus === "error" || f.extractionStatus === "unsupported") && f.extractionMessage ? (
                      <span style={{ fontSize: 11, color: C.muted }}>{f.extractionMessage}</span>
                    ) : null}
                  </div>
                ))}
              </div>
            )
          ) : (
            <SurfaceStateView state={filesQ} onRetry={filesQ.retry} />
          )}
        </>
      )}
    </SurfacePage>
  );
}

/* ── Automations ────────────────────────────────────────────────────────── */

interface AutomationRow {
  id: string;
  name: string;
  status: string;
  schedule?: string | undefined;
}

export function AutomationsSurface() {
  const C = useBrandColors();
  const { locale } = useLocale();
  const q = useSurfaceQuery<AutomationRow[]>("/api/cowork/automations", (d) =>
    (Array.isArray(d.automations) ? (d.automations as Record<string, unknown>[]) : []).map((a) => ({
      id: String(a.automationId ?? a.id ?? ""),
      name: String(a.name ?? a.title ?? a.automationId ?? a.id ?? ""),
      status: String(a.status ?? "active"),
      schedule: typeof a.schedule === "string" ? a.schedule : undefined,
    })),
  );
  return (
    <SurfacePage
      route="automation"
      title={productText("navAutomation", locale)}
      subtitle={locale === "hi" ? "कैनॉनिकल cowork automation" : "Canonical cowork automations"}
    >
      {q.kind === "ready" ? (
        q.data.length === 0 ? (
          <EmptyState />
        ) : (
          <Grid>
            {q.data.map((a) => (
              <div key={a.id} style={cardStyle(C)}>
                <div style={{ fontSize: 13, fontWeight: 650, color: C.fg }}>{a.name}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
                  {a.status}
                  {a.schedule ? ` · ${a.schedule}` : ""}
                </div>
              </div>
            ))}
          </Grid>
        )
      ) : (
        <SurfaceStateView state={q} onRetry={q.retry} />
      )}
    </SurfacePage>
  );
}

/* ── Apps ───────────────────────────────────────────────────────────────── */

interface AppRow {
  appId: string;
  displayName: string;
  connectionStatus: string;
  publicApiStatus: string;
  allowed: number;
}

export function AppsSurface() {
  const C = useBrandColors();
  const { locale } = useLocale();
  const q = useSurfaceQuery<AppRow[]>("/api/public/apps", (d) =>
    (Array.isArray(d.apps) ? (d.apps as Record<string, unknown>[]) : []).map((a) => ({
      appId: String(a.appId ?? ""),
      displayName: String(a.displayName ?? a.appId ?? ""),
      connectionStatus: String(a.connectionStatus ?? ""),
      publicApiStatus: String(a.publicApiStatus ?? ""),
      allowed: Array.isArray(a.allowedPublicCapabilities) ? a.allowedPublicCapabilities.length : 0,
    })),
  );
  return (
    <SurfacePage
      route="apps"
      title={productText("navApps", locale)}
      subtitle={locale === "hi" ? "सर्वेन app federation — public preview" : "Seven-app federation — public preview"}
    >
      {q.kind === "ready" ? (
        q.data.length === 0 ? (
          <EmptyState />
        ) : (
          <Grid>
            {q.data.map((a) => (
              <div key={a.appId} style={cardStyle(C)}>
                <div style={{ fontSize: 13, fontWeight: 650, color: C.fg }}>{a.displayName}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{a.appId}</div>
                <div style={{ fontSize: 11, marginTop: 6 }}>
                  <span
                    style={{
                      padding: "1px 8px",
                      borderRadius: 999,
                      border: `1px solid ${C.border}`,
                      color: C.muted,
                    }}
                  >
                    {a.publicApiStatus === "preview_only" ? productText("surfacePreview", locale) : a.publicApiStatus}
                  </span>
                  <span style={{ color: C.muted, marginLeft: 8 }}>
                    {a.allowed} {locale === "hi" ? "कैपेबिलिटी" : "capabilities"}
                  </span>
                </div>
              </div>
            ))}
          </Grid>
        )
      ) : (
        <SurfaceStateView state={q} onRetry={q.retry} />
      )}
    </SurfacePage>
  );
}

/* ── Code ───────────────────────────────────────────────────────────────── */

interface CodingTaskRow {
  taskId: string;
  status: string;
  description: string;
}

interface DesktopBindingRow {
  desktopId: string;
  status: string;
  lastHeartbeatAt: string | null;
}

/*
 * Web→Desktop handoff card. The browser never performs trusted local
 * coding/desktop execution — it only shows the truthful device state and, when
 * a bound Desktop is online, hands off via `smartshikka://handoff/<id>` whose
 * only payload is the opaque one-time id (Desktop resolves the authenticated
 * project/context server-side; no token or secret ever travels in the URL).
 */
function CodeDesktopHandoffCard() {
  const C = useBrandColors();
  const { locale } = useLocale();
  const { token } = useAuth();
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ kind: "launched" | "error"; text: string } | null>(null);
  const desktopQ = useSurfaceQuery<DesktopBindingRow[]>("/api/mobile/desktop/status", (d) =>
    (Array.isArray(d.bindings) ? (d.bindings as Record<string, unknown>[]) : []).map((b) => ({
      desktopId: String(b.desktopId ?? ""),
      status: String(b.status ?? ""),
      lastHeartbeatAt: b.lastHeartbeatAt ? String(b.lastHeartbeatAt) : null,
    })),
  );

  const openInDesktop = () => {
    if (!token || busy) return;
    setBusy(true);
    setNotice(null);
    api
      .mobileDesktopHandoff(token, "code")
      .then((res) => {
        if (res.ok && res.data?.deepLink) {
          setNotice({
            kind: "launched",
            text: locale === "hi"
              ? "Desktop खोला जा रहा है… न खुले तो दोबारा क्लिक करें"
              : "Opening Desktop… click again if it does not launch",
          });
          // The OS hands the scheme to the installed app; nothing else is sent.
          window.location.href = res.data.deepLink;
        } else {
          setNotice({ kind: "error", text: locale === "hi" ? `हैंडऑफ़ विफल: ${res.error ?? "unknown"}` : `Handoff failed: ${res.error ?? "unknown"}` });
        }
      })
      .catch(() => setNotice({ kind: "error", text: locale === "hi" ? "हैंडऑफ़ विफल" : "Handoff failed" }))
      .finally(() => setBusy(false));
  };

  const title = locale === "hi" ? "Coding execution" : "Coding execution";
  const active = desktopQ.kind === "ready" ? desktopQ.data.filter((b) => b.status === "active") : [];
  const inactive = desktopQ.kind === "ready" ? desktopQ.data : [];

  return (
    <div style={{ ...cardStyle(C), marginBottom: 14 }} data-testid="code-desktop-handoff">
      <div style={{ fontSize: 13, fontWeight: 600, color: C.fg, marginBottom: 6 }}>{title}</div>
      {desktopQ.kind === "ready" ? (
        active.length > 0 ? (
          <>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>
              {locale === "hi"
                ? "Smart Shikka Desktop जुड़ा है — execution authority डेस्कटॉप के पास रहेगी"
                : "Smart Shikka Desktop is connected — it remains the execution authority"}
            </div>
            <button
              type="button"
              onClick={openInDesktop}
              disabled={busy}
              style={{
                padding: "7px 14px",
                borderRadius: 999,
                border: `1px solid ${C.primary}`,
                background: "transparent",
                color: C.primary,
                fontSize: 12,
                fontWeight: 600,
                cursor: busy ? "default" : "pointer",
              }}
            >
              {busy
                ? locale === "hi" ? "खुल रहा है…" : "Opening…"
                : locale === "hi" ? "Smart Shikka Desktop में खोलें" : "Open in Smart Shikka Desktop"}
            </button>
            {notice ? (
              <span role={notice.kind === "error" ? "alert" : "status"} style={{ marginLeft: 10, fontSize: 11, color: notice.kind === "error" ? "#b3261e" : C.muted }}>
                {notice.text}
              </span>
            ) : null}
          </>
        ) : inactive.length > 0 ? (
          <div style={{ fontSize: 12, color: C.muted }}>
            <strong style={{ color: C.fg }}>
              {locale === "hi" ? "Reconnect Desktop" : "Reconnect Desktop"}
            </strong>
            {" · "}
            {locale === "hi"
              ? `Desktop जुड़ा नहीं है (${inactive.map((b) => b.status || "offline").join(", ")}${inactive[0]?.lastHeartbeatAt ? ` · अंतिम heartbeat ${inactive[0].lastHeartbeatAt}` : ""}) — coding execution अभी उपलब्ध नहीं`
              : `Desktop is not connected (${inactive.map((b) => b.status || "offline").join(", ")}${inactive[0]?.lastHeartbeatAt ? ` · last heartbeat ${inactive[0].lastHeartbeatAt}` : ""}) — coding execution is not available`}
          </div>
        ) : (
          <>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>
              {locale === "hi"
                ? "Coding execution के लिए Smart Shikka Desktop ज़रूरी है — ब्राउज़र स्थानीय coding नहीं चलाता"
                : "Coding execution requires Smart Shikka Desktop — the browser never runs local coding"}
            </div>
            <DesktopDownloadCta />
          </>
        )
      ) : desktopQ.kind === "unavailable" ? (
        <div style={{ fontSize: 12, color: C.muted }}>
          {locale === "hi"
            ? "Desktop स्थिति केवल owner session में दिखती है"
            : "Desktop status is visible to the owner session only"}
        </div>
      ) : (
        <SurfaceStateView state={desktopQ} onRetry={desktopQ.retry} />
      )}
    </div>
  );
}

export function CodeSurface() {
  const C = useBrandColors();
  const { locale } = useLocale();
  const projectsQ = useSurfaceQuery<ProjectRow[]>("/api/projects", (d) =>
    (Array.isArray(d.projects) ? (d.projects as Record<string, unknown>[]) : []).map((p) => ({
      projectId: String(p.projectId ?? ""),
      displayName: String(p.displayName ?? p.projectId ?? ""),
      repository: String(p.repository ?? ""),
      visibility: String(p.visibility ?? ""),
      defaultBranch: String(p.defaultBranch ?? ""),
    })),
  );
  const projectId = projectsQ.kind === "ready" ? projectsQ.data[0]?.projectId ?? "" : "";
  const tasksQ = useSurfaceQuery<CodingTaskRow[]>(
    projectId ? `/api/projects/${encodeURIComponent(projectId)}/coding/tasks` : null,
    (d) =>
      (Array.isArray(d.tasks) ? (d.tasks as Record<string, unknown>[]) : []).map((t) => ({
        taskId: String(t.taskId ?? t.id ?? ""),
        status: String(t.status ?? ""),
        description: String(t.description ?? t.title ?? ""),
      })),
  );

  return (
    <SurfacePage
      route="code"
      title={productText("navCode", locale)}
      subtitle={
        locale === "hi"
          ? "कैनॉनिकल coding tasks — निर्माण Chat के Code mode से होता है"
          : "Canonical coding tasks — creation happens from Chat's Code mode"
      }
    >
      <CodeDesktopHandoffCard />
      {projectsQ.kind === "ready" && !projectId ? (
        <EmptyState />
      ) : projectsQ.kind !== "ready" ? (
        <SurfaceStateView state={projectsQ} onRetry={projectsQ.retry} />
      ) : tasksQ.kind === "ready" ? (
        tasksQ.data.length === 0 ? (
          <EmptyState />
        ) : (
          <Grid>
            {tasksQ.data.map((t) => (
              <div key={t.taskId} style={cardStyle(C)}>
                <div style={{ fontSize: 12, color: C.fg }}>{t.description || t.taskId}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>{t.status}</div>
              </div>
            ))}
          </Grid>
        )
      ) : (
        <SurfaceStateView state={tasksQ} onRetry={tasksQ.retry} />
      )}
    </SurfacePage>
  );
}

/* ── Design ─────────────────────────────────────────────────────────────── */

export function DesignSurface() {
  const C = useBrandColors();
  const { locale } = useLocale();
  return (
    <SurfacePage
      route="design"
      title={productText("navDesign", locale)}
      subtitle={productText("surfacePreview", locale)}
    >
      <div style={{ ...cardStyle(C), maxWidth: 560 }}>
        <p style={{ margin: "0 0 8px", fontSize: 13, color: C.fg }}>
          {productText("designAiAssisted", locale)}
        </p>
        <p style={{ margin: "0 0 14px", fontSize: 12, color: C.muted }}>
          {productText("designAiUnsupported", locale)}
        </p>
        <a
          href="#chat"
          style={{
            display: "inline-block",
            padding: "7px 14px",
            borderRadius: 999,
            border: `1px solid ${C.primary}`,
            color: C.primary,
            fontSize: 12,
            textDecoration: "none",
          }}
        >
          {productText("openInChat", locale)}
        </a>
      </div>
    </SurfacePage>
  );
}

/* ── Activity ───────────────────────────────────────────────────────────── */

export function ActivitySurface() {
  const { token } = useAuth();
  const { locale } = useLocale();
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "error"; detail: string }
    | { kind: "unavailable" }
    | {
        kind: "ready";
        entries: ReturnType<typeof buildActivityLedger>;
        sources: string[];
        failedSources: string[];
      }
  >({ kind: "loading" });

  useEffect(() => {
    if (!token) {
      setState({ kind: "unavailable" });
      return;
    }
    let cancelled = false;
    setState({ kind: "loading" });
    void (async () => {
      const sources: string[] = [];
      const failedSources: string[] = [];
      const assistantTurns: LedgerAssistantTurn[] = [];
      const runs: LedgerCoworkRun[] = [];
      const approvals: LedgerApproval[] = [];
      const usage: LedgerUsageEvent[] = [];

      const convs = await surfaceGetJson(token, "/api/chat/conversations");
      if (convs.ok && convs.data) {
        sources.push("chat.messages");
        const list = Array.isArray(convs.data.conversations)
          ? (convs.data.conversations as Record<string, unknown>[])
          : [];
        // Bounded: the latest 5 conversations' durable messages feed the
        // assistant-turn rows — the ledger never walks the whole store.
        for (const c of list.slice(0, 5)) {
          const convId = String(c.conversationId ?? c.id ?? "");
          if (!convId) continue;
          const msgs = await surfaceGetJson(
            token,
            `/api/chat/conversations/${encodeURIComponent(convId)}/messages`,
          );
          if (msgs.ok && msgs.data) {
            const arr = Array.isArray(msgs.data.messages)
              ? (msgs.data.messages as Record<string, unknown>[])
              : [];
            for (const m of arr) {
              if (String(m.role) !== "assistant") continue;
              const turn = assistantTurnFromMessage(m);
              if (turn) assistantTurns.push(turn);
            }
          }
        }
      } else {
        failedSources.push("chat.messages");
      }

      const runsRes = await surfaceGetJson(token, "/api/cowork/runs");
      if (runsRes.ok && runsRes.data) {
        sources.push("cowork.runs");
        const list = Array.isArray(runsRes.data.runs) ? (runsRes.data.runs as Record<string, unknown>[]) : [];
        for (const r of list) {
          runs.push({
            runId: String(r.runId ?? ""),
            status: String(r.status ?? ""),
            title: typeof r.title === "string" ? r.title : String(r.runId ?? ""),
            updatedAt: typeof r.updatedAt === "number" ? r.updatedAt : undefined,
          });
        }
      } else if (runsRes.status === 401 || runsRes.status === 403) {
        // privileged-only source — honest skip, not an error
      } else {
        failedSources.push("cowork.runs");
      }

      const apprRes = await surfaceGetJson(token, "/api/cowork/approvals");
      if (apprRes.ok && apprRes.data) {
        sources.push("cowork.approvals");
        const list = Array.isArray(apprRes.data.approvals)
          ? (apprRes.data.approvals as Record<string, unknown>[])
          : [];
        for (const a of list) {
          approvals.push({
            approvalId: String(a.approvalId ?? a.id ?? ""),
            status: String(a.status ?? "pending"),
            actionLabel:
              typeof a.actionLabel === "string" ? a.actionLabel : String(a.approvalId ?? a.id ?? ""),
            updatedAt: typeof a.updatedAt === "number" ? a.updatedAt : undefined,
          });
        }
      } else if (apprRes.status !== 401 && apprRes.status !== 403) {
        failedSources.push("cowork.approvals");
      }

      const usageRes = await surfaceGetJson(token, "/api/usage");
      if (usageRes.ok && usageRes.data) {
        sources.push("usage.ledger");
        const list = Array.isArray(usageRes.data.events)
          ? (usageRes.data.events as Record<string, unknown>[])
          : [];
        for (const u of list.slice(0, 100)) {
          usage.push({
            id: String(u.id ?? u.eventId ?? ""),
            label: String(u.kind ?? u.label ?? u.eventType ?? "usage"),
            at: typeof u.at === "number" ? u.at : typeof u.createdAt === "number" ? u.createdAt : 0,
            status: typeof u.status === "string" ? u.status : undefined,
          });
        }
      } else if (usageRes.status !== 401 && usageRes.status !== 403 && usageRes.status !== 503) {
        failedSources.push("usage.ledger");
      }

      if (cancelled) return;
      if (sources.length === 0 && failedSources.length > 0) {
        setState({ kind: "error", detail: failedSources.join(", ") });
        return;
      }
      setState({
        kind: "ready",
        entries: buildActivityLedger({ assistantTurns, runs, approvals, usage }),
        sources,
        failedSources,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <SurfacePage
      route="activity"
      title={productText("navActivity", locale)}
      subtitle={productText("ledgerSource", locale)}
    >
      {state.kind === "ready" && state.failedSources.length > 0 ? (
        <p style={{ fontSize: 11, color: "#b45309", marginTop: 0 }}>
          {locale === "hi" ? "कुछ स्रोत लोड नहीं हुए" : "Some sources failed"}: {state.failedSources.join(", ")}
        </p>
      ) : null}
      {state.kind === "ready" ? (
        <ActivityLedgerView locale={locale} entries={state.entries} />
      ) : (
        <SurfaceStateView state={state} />
      )}
    </SurfacePage>
  );
}
